import React, { useState } from 'react';
import StepForm from './StepForm/StepForm';
import StepDetails from './StepDetails/StepDetails';
import EmailContent from './EmailContent/EmailContent';
import EmailDetails from './EmailDetails/EmailDetails';
import DecisionGateway from './DecisionGateway/DecisionGateway';
import { ProcessMapNodeCategories } from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/processMapNodeCategories';
import StepTags from './StepTags/StepTags';
import { getService } from '@Client/runner.hooks/useService';
import { CustomTabs } from '@Shared.Angular/flowingly.components/CustomTabs/CustomTabs';
import IconButton from '@Shared.Angular/flowingly.components/buttons/IconButton';
import NamedAvatar from './NamedAvatar/NamedAvatar';
import { CustomTabPanel } from '@Shared.Angular/flowingly.components/CustomTabs/CustomTab/CustomTabPanel';
import ActivityTaskType from '@Shared.Angular/@types/core/contracts/queryModel/workflow/steps/activityTaskType';
import IProcessMapNodeViewModel from '@Shared.Angular/@types/processMapNodeViewModel';

type Props = {
  selectedNode: IProcessMapNodeViewModel;
  onClose: () => void;
};

export const RightPanel = (props: Props) => {
  const { selectedNode, onClose } = props;

  const appConfig = getService('APP_CONFIG');

  const decisionGateway = selectedNode?.decisionGateway;
  const stepName = selectedNode && selectedNode.stepName;
  const stepAssignee = selectedNode && selectedNode.stepActor;

  const getTabsConfig = () => {
    if (selectedNode?.stepType === ActivityTaskType.CUSTOM_EMAIL) {
      return {
        tabs: [
          { label: 'Recipients', id: CustomEmailTabId.RECIPIENTS },
          { label: 'Template', id: CustomEmailTabId.TEMPLATE }
        ],
        tabPanels: [{ component: EmailDetails }, { component: EmailContent }],
        headerData: [{ title: 'Step Name', value: stepName }],
        defaultTab: CustomEmailTabId.TEMPLATE
      };
    }
    if (selectedNode?.category === ProcessMapNodeCategories.DecisionGateway) {
      return {
        tabPanels: [{ component: DecisionGateway }],
        headerData: [
          {
            title: 'Decision Name',
            value: decisionGateway?.name || 'Not Specified'
          }
        ],
        defaultTab: null
      };
    }
    return {
      tabs: [
        { label: 'Step Details', id: StepTabId.DETAILS },
        { label: 'Step Form', id: StepTabId.FORM },
        ...(appConfig.enableTagging
          ? [{ label: 'Tags', id: StepTabId.TAGS }]
          : [])
      ],
      tabPanels: [
        { component: StepDetails },
        { component: StepForm },
        { component: StepTags }
      ],
      headerData: [
        {
          title: 'Step Assignee',
          value: stepAssignee,
          showAvatar: true
        },
        { title: 'Step Name', value: stepName }
      ],
      defaultTab: StepTabId.FORM
    };
  };

  const { tabs, tabPanels, headerData, defaultTab } = getTabsConfig();
  const [selectedTabId, setSelectedTabId] = useState<string | null>(defaultTab);

  return (
    <div id="process-map-rightpanel">
      <div className={'process-map-v2-view-right-panel-header-container'}>
        <IconButton
          iconClass="fa-xmark"
          title="Close panel"
          onClick={onClose}
          border={false}
          classNames="right-panel-close-button"
          testId="right-panel-close-button"
        />
        {headerData.map((headerItem) => (
          <div
            className="process-map-v2-view-right-panel-header-row"
            key={headerItem.title}
          >
            <div className="process-map-v2-view-right-panel-header-label-cell">
              {headerItem.title}
            </div>
            <div className="process-map-v2-view-right-panel-header-content-cell">
              {headerItem.showAvatar ? (
                <NamedAvatar fullName={headerItem.value} />
              ) : (
                <p>{headerItem.value}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      {tabs?.length > 0 ? (
        <CustomTabs
          selectedTabId={selectedTabId}
          onChange={setSelectedTabId}
          tabItems={tabs}
        />
      ) : (
        <div className="process-map-v2-view-tabpanel-notabs-container"></div>
      )}
      {tabPanels.map(({ component: Component }, index) => (
        <CustomTabPanel
          key={index}
          isShown={
            !tabs || tabs.length === 0 || selectedTabId === tabs[index]?.id
          }
          tabId={tabs?.[index]?.id}
        >
          <Component selectedNode={selectedNode} />
        </CustomTabPanel>
      ))}
    </div>
  );
};

enum StepTabId {
  DETAILS = 'step-details',
  FORM = 'step-form',
  TAGS = 'step-tags'
}

enum CustomEmailTabId {
  RECIPIENTS = 'custom-email-recipients',
  TEMPLATE = 'custom-email-template'
}
